import { useEffect, useState } from 'react'

// Percorsi relativi (stessa origine della pagina): un unico reverse proxy
// davanti a frontend/backend-consumer/core (vedi proxy/nginx) fa da router
// per path — niente più porta fissa nel codice, funziona identico su LAN,
// tunnel pubblico, o qualunque hostname da cui si apre la pagina.
const API_BASE = ''
const CORE_API_BASE = ''

async function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result.split(',')[1] || '')
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

// Recupera gli allegati originali di un job per la review — condiviso da
// Dashboard.jsx e Coda.jsx.
//
// TUTTI i PDF dell'allegato vengono scaricati e restituiti insieme
// (`pdfDocuments`, un array di {filename, base64}) — non più uno alla volta
// con freccette avanti/indietro (approccio precedente, tolto su richiesta
// 2026-08-13: con allegati multipli si vedeva sempre e solo il primo, e
// l'evidenziatore non poteva funzionare su un documento non visibile). Il
// chiamante li rende impilati in un'unica colonna scrollabile, uno per
// documento — vedi Dashboard.jsx/Coda.jsx.
//
// Se non c'è nessun PDF, il primo allegato di qualsiasi altro formato
// (xlsx/docx/csv/txt/immagini) va in `originalFile`, per un download del
// file vero — PdfViewer non sa renderizzare nativamente questi formati, ma
// il testo già parsato (`job.body`) resta comunque visibile come anteprima.
//
// Due effect separati apposta: il primo scarica solo l'ELENCO (leggero) al
// cambio job; il secondo scarica i BYTE di tutti i PDF (o del singolo
// allegato non-PDF di fallback).
export default function useAttachmentPreview(job) {
  // [{filename, base64}, ...] — uno per ogni allegato PDF del job.
  const [pdfDocuments, setPdfDocuments] = useState([])
  const [originalFile, setOriginalFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [attachments, setAttachments] = useState([])
  // Job a cui appartiene l'`attachments` attuale — impostato SOLO insieme a
  // setAttachments (stesso posto, stesso momento), mai da solo. Serve
  // all'effect 2 per riconoscere un giro "transitorio": al cambio job,
  // l'effect 1 riparte per il job NUOVO ma finché la sua fetch non risponde
  // `attachments` è ancora la lista del job VECCHIO — e l'effect 2 gira
  // comunque (dipende anche lui da job?.job_id), rischiando di scaricare i
  // byte degli allegati del job vecchio ma con l'URL del job nuovo.
  const [attachmentsJobId, setAttachmentsJobId] = useState(null)

  const pdfAttachments = attachments.filter((a) => a.format === 'pdf')

  // Effect 1: elenco allegati al cambio job.
  useEffect(() => {
    let cancelled = false
    setPdfDocuments([])
    setOriginalFile(null)
    setAttachments([])
    setAttachmentsJobId(null)
    setLoading(!!job?.job_id && !job?.manual_pdf_base64)

    if (!job?.job_id || job.manual_pdf_base64) return

    const tryListFrom = async (base) => {
      const res = await fetch(`${base}/api/jobs/${job.job_id}/attachments`)
      if (!res.ok) throw new Error(`status ${res.status}`)
      const { attachments: list } = await res.json()
      return list || []
    }

    const run = async () => {
      try {
        let list = await tryListFrom(API_BASE)
        if (!list.length) list = await tryListFrom(CORE_API_BASE)
        if (!cancelled) {
          setAttachments(list)
          setAttachmentsJobId(job.job_id)
        }
      } catch (err) {
        console.error("Errore nel recupero dell'elenco allegati:", err.message)
        if (!cancelled) setLoading(false)
      }
    }

    run()
    return () => { cancelled = true }
  }, [job?.job_id])

  // Effect 2: byte di TUTTI i PDF (in parallelo) — o del singolo allegato
  // non-PDF di fallback se non ce n'è nessuno.
  useEffect(() => {
    let cancelled = false

    if (job?.manual_pdf_base64) {
      // Ordine caricato manualmente: il PDF è già in memoria lato client.
      setPdfDocuments([{ filename: 'documento.pdf', base64: job.manual_pdf_base64 }])
      setLoading(false)
      return
    }
    if (!job?.job_id) return
    // Giro transitorio (vedi commento su attachmentsJobId sopra): aspetta
    // che l'effect 1 finisca per QUESTO job prima di scaricare i byte.
    if (attachmentsJobId !== job.job_id) return

    const fetchBytes = async (filename) => {
      const tryFrom = async (base) => {
        const res = await fetch(
          `${base}/api/jobs/${job.job_id}/attachments/${encodeURIComponent(filename)}`
        )
        if (!res.ok) throw new Error(`status ${res.status}`)
        return blobToBase64(await res.blob())
      }
      return tryFrom(API_BASE).catch(() => tryFrom(CORE_API_BASE))
    }

    if (pdfAttachments.length > 0) {
      setLoading(true)
      setOriginalFile(null)
      Promise.all(
        pdfAttachments.map((a) =>
          fetchBytes(a.filename)
            .then((base64) => ({ filename: a.filename, base64 }))
            .catch((err) => {
              console.error(`Errore nel recupero dell'allegato "${a.filename}":`, err.message)
              return null
            })
        )
      ).then((docs) => {
        if (!cancelled) setPdfDocuments(docs.filter(Boolean))
      }).finally(() => {
        if (!cancelled) setLoading(false)
      })
      return () => { cancelled = true }
    }

    setPdfDocuments([])
    const chosen = attachments[0]

    if (!chosen) {
      // Elenco non ancora arrivato (effect 1 in corso) — oppure nessun
      // allegato reale e siamo in DEV MOCK (né frontend-consumer né Core
      // raggiungibili, vedi job.mock_pdf_url).
      if (attachments.length === 0 && job.mock_pdf_url) {
        fetch(job.mock_pdf_url)
          .then((res) => res.blob())
          .then(blobToBase64)
          .then((base64) => {
            if (!cancelled) { setPdfDocuments([{ filename: 'documento.pdf', base64 }]); setLoading(false) }
          })
          .catch((err) => {
            console.warn('[DEV MOCK] impossibile caricare il PDF di test:', err.message)
            if (!cancelled) setLoading(false)
          })
      }
      return () => { cancelled = true }
    }

    setLoading(true)
    setOriginalFile(null)
    fetchBytes(chosen.filename)
      .then((base64) => {
        if (!cancelled) setOriginalFile({ filename: chosen.filename, format: chosen.format, base64 })
      })
      .catch((err) => {
        console.error("Errore nel recupero dell'allegato originale:", err.message)
      })
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.job_id, attachments, attachmentsJobId])

  return {
    pdfDocuments,
    originalFile,
    loading,
  }
}
